<template>
  <div v-if="isVisible"
    class="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 font-roboto w-full">
    <div
      class="bg-white rounded-[15px] shadow-2xl mobile:!w-full tablet:!w-[75%] max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">

      <!-- Header -->
      <div class="rounded-[5px] bg-[#092E87] px-2 py-3 m-[15px] flex justify-between items-center relative">
        <h2 class="text-white text-lg font-normal mb-0">Available Financing Plans</h2>
        <button @click="close" class="transition-colors h-[20px] w-[20px] absolute right-2.5 top-2.5">
          <img src="../../assets/images/modal-cross-icon.png" alt="icon" class="object-contain w-full h-full">
        </button>
        <!-- Triangle pointer decoration from image -->
        <div
          class="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[10px] border-t-[#092E87]">
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto mr-[15px]">
        <div class="rounded-[10px] p-2.5 shadow-[0_0_5px_2px_#cccccc6b] mr-[20px] m-[15px]">
          <!-- onlu for desktop -->
          <div v-for="(section, sIdx) in financialPlans" :key="sIdx"
            class="rounded-lg overflow-hidden mobile:hidden laptop:block" :class="sIdx > 0 ? 'mt-[15px]' : ''">

            <!-- Category Header (Category 4 & 5 have an extra top bar) -->
            <div v-if="section.extraHeaders.length > 0"
              class="bg-[#D9E9FF] text-[#333] text-center py-2.5 text-sm font-bold rounded-t-[5px] border-b relative -bottom-2.5">
              {{ section.category }}
            </div>

            <table class="w-full text-left text-xs border-separate border-spacing-y-1.5 ">
              <thead>
                <tr>
                  <th class="px-2 text-[14px] py-[12px] w-[10%] largeDesktop:w-[7%] bg-[#092E87] text-white rounded-l-[5px]" :class="section.extraHeaders.length > 0 ? ' !rounded-t-[0px]' : ''">Plan No.</th>
                  <th v-if="section.extraHeaders.length <= 0" v-html="section.category" class="px-2 text-[14px] py-[12px] w-[45%] bg-[#092E87] text-white description--text">
                  </th>
                  <th v-for="h in section.extraHeaders" :key="h"
                    class="px-2 text-[14px] py-[12px] text-left font-bold bg-[#092E87] text-white w-[15%]">
                    {{ h }}
                  </th>
                  <th class="px-2 text-[14px] py-[12px] text-center font-bold min-w-[20%] border-l border-solid !bg-[#F2F9FF] !text-[#092E87]" :class="section.extraHeaders.length > 0 ? 'border-[#D9EAFD]' : 'border-white'">Min Loan Amt</th>
                  <th class="px-2 text-[14px] py-[12px] text-center font-bold min-w-[20%] border-l border-solid !bg-[#F2F9FF] !text-[#092E87]" :class="section.extraHeaders.length > 0 ? 'border-[#D9EAFD]' : 'border-white'">Max Loan Amt</th>
                  <th class="px-2 text-[14px] py-[12px] w-[5%] text-white !bg-[#F2F9FF] rounded-r-[5px]" :class="section.extraHeaders.length > 0 ? '!rounded-t-[0px]' : ''"></th>
                </tr>
              </thead>
              <tbody class="table-row-group space-y-2">
                <tr v-for="(plan, pIdx) in section.plans" :key="pIdx"
                  class="border-b last:border-0 hover:bg-gray-50 cursor-pointer" @click="selectedPlanId = plan">
                  <td class="p-2 text-[14px] w-[10%] largeDesktop:w-[7%] font-medium rounded-l-[5px] text-gray-600 border-solid border-t border-b border-l border-r-0 "
                  :class="selectedPlanId?.id == plan.id ? 'border-[#FE5000]' : 'border-[#ccc]'">{{ plan.plan_number || 'N/A' }}</td>
                  <td v-if="plan.description && section.extraHeaders.length == 0" v-html="plan.description" class="p-2 text-[14px] w-[45%] text-gray-700 border-y border-solid"
                  :class="selectedPlanId?.id == plan.id ? 'border-[#FE5000]' : 'border-[#ccc]'">
                  </td>

                  <!-- Dynamic columns for Interest Rate, Payment Factor -->
                  
                  <td v-if="section.extraHeaders && section.extraHeaders.includes('Interest Rate')" class="p-2 text-[14px] text-gray-700 border-y border-solid w-[15%]"
                  :class="selectedPlanId?.id == plan.id ? 'border-[#FE5000]' : 'border-[#ccc]'">
                    {{ $helper.formattedPercentage(plan.interest_rate) || '-' }}
                  </td>

                  <td v-if="section.extraHeaders && section.extraHeaders.includes('Loan Term')" class="p-2 text-[14px] text-gray-700 border-y border-solid w-[15%]"
                  :class="selectedPlanId?.id == plan.id ? 'border-[#FE5000]' : 'border-[#ccc]'">
                    {{ plan.description || '-' }}
                  </td>

                  <td v-if="section.extraHeaders && section.extraHeaders.includes('Payment Factor')" class="p-2 text-[14px] text-gray-700 border-y border-solid w-[15%]"
                  :class="selectedPlanId?.id == plan.id ? 'border-[#FE5000]' : 'border-[#ccc]'">
                    {{ $helper.formattedPercentage(plan.payment_factor) || '-' }}
                  </td>
                  <td class="p-2 text-[14px] min-w-[20%] text-center text-gray-700 border-y border-solid"
                  :class="selectedPlanId?.id == plan.id ? 'border-[#FE5000]' : 'border-[#ccc]'">${{ $helper.getFormattedAmount_(plan.min) || 0 }}</td>
                  <td class="p-2 text-[14px] min-w-[20%] text-center text-gray-700 border-y border-solid"
                  :class="selectedPlanId?.id == plan.id ? 'border-[#FE5000]' : 'border-[#ccc]'">${{ $helper.getFormattedAmount_(plan.max) || 0 }}</td>
                  <td class="p-2 text-[14px] w-[5%] border-solid border-t border-b border-r rounded-r-[5px]"
                  :class="selectedPlanId?.id == plan.id ? 'border-[#FE5000]' : 'border-[#ccc]'">
                    <div class="w-5 h-5 flex justify-center items-center rounded-full border-1 transition-all"
                      :class="selectedPlanId?.id == plan.id ? 'border-[#FE5000] bg-[#FE5000]/20' : 'border-[#CCCCCC] bg-[#CCCCCC]/30'">
                      <div v-if="selectedPlanId?.id === plan.id" class="w-2.5 h-2.5 rounded-full bg-[#FE5000]"></div>
                      <div v-else class="w-2.5 h-2.5 rounded-full bg-[#CCCCCC]"></div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- only for mobile/tablets -->
          <div v-for="(section, sIdx) in financialPlans" :key="sIdx" class="rounded-lg  mobile:block laptop:hidden" 
          :class="sIdx > 0 ? 'mt-[15px]' : '' ">

            <div v-if="(section.extraHeaders.length > 0) || section.category" v-html="section.category"
              class="bg-[#092E87] text-white text-left p-2.5 text-[13px] font-bold rounded-[5px] relative font-lato description--text">
            </div>
            <div v-for="(plan, pIdx) in section.plans" :key="pIdx" @click="selectedPlanId = plan"
              class="w-full border-[1px] border-solid rounded-[10px] p-2.5 !pt-0 font-lato" :class="[selectedPlanId?.id == plan.id ? 'border-[#FE5000]' : 'border-[#EFEFEF]',
              pIdx === 0 ? 'mt-1' : 'mt-2.5',
              ]">
              <div class="py-2 flex justify-between items-center border-b border-[#EFEFEF]">
                <span v-if="plan.plan_number" class="text-[14px] font-bold text-[#092E87]">Plan No.:</span>
                <span v-else class="text-[14px] font-normal text-[#333333]">N/A</span>
                <span class="text-[14px] font-bold text-[#333333]">{{ plan.plan_number || '-' }}</span>
              </div>
              <div v-if="section.extraHeaders.length == 0" class="py-2 flex flex-col border-b border-[#EFEFEF]">
                <span v-if="plan.description" class="text-[14px] font-bold text-[#092E87]">Plan:</span>
                <span v-else class="text-[14px] font-normal text-[#333333]">N/A</span>
                <span v-html="plan.description" class="text-[14px] font-normal text-[#333333]"></span>
              </div>
              <div v-if="section.extraHeaders && section.extraHeaders.includes('Interest Rate')"
                class="py-2 flex justify-between items-center border-b border-[#EFEFEF]">
                <span class="text-[14px] font-normal text-[#092E87]">Interest Rate</span>
                <span class="text-[14px] font-normal text-[#333333]">{{ $helper.formattedPercentage(plan.interest_rate) || '-' }}</span>
              </div>
              <div v-if="section.extraHeaders && section.extraHeaders.includes('Loan Term')"
                class="py-2 flex justify-between items-center border-b border-[#EFEFEF]">
                <span class="text-[14px] font-normal text-[#092E87]">Loan Term</span>
                <span class="max-w-[70%] text-right text-[14px] font-normal text-[#333333]">{{ plan.description || '-' }}</span>
              </div>
              <div v-if="section.extraHeaders && section.extraHeaders.includes('Payment Factor')"
                class="py-2 flex justify-between items-center border-b border-[#EFEFEF]">
                <span class="text-[14px] font-normal text-[#092E87]">Payment Factor</span>
                <span class="text-[14px] font-normal text-[#333333]">{{ $helper.formattedPercentage(plan.payment_factor) || '-' }}</span>
              </div>
              <div class="flex justify-between items-center p-2.5 mt-1.5 bg-[#F2F9FF]">
                <span class="text-[14px] font-normal text-[#092E87]">Min Loan Amt</span>
                <p class="mb-0 text-[14px] font-bold text-[#333333]">${{ $helper.getFormattedAmount_(plan.min) || 0 }}</p>
              </div>
              <div class="flex justify-between items-center p-2.5 mt-1.5 bg-[#F2F9FF]">
                <span class="text-[14px] font-normal text-[#092E87]">Max Loan Amt</span>
                <p class="mb-0 text-[14px] font-bold text-[#333333]">${{ $helper.getFormattedAmount_(plan.max) || 0 }}</p>
              </div>
            </div>
          </div>
        </div>
        <p class="text-[12px] laptop:text-[14px] text-[#333] m-[20px] text-center">
          Version 2.1.19 *Loan Programs, interest rates, and terms are subject to change without notice.     Please contact 
                      Service Finance Company, LLC with any additional questions and for pricing.
        </p>
      </div>

      <!-- Footer Buttons -->
      <div class="p-4 flex justify-center gap-4">
        <button @click="close"
          class="px-10 py-2 border-solid border-[1px] border-[#FE5000] text-[#FE5000] rounded-full font-medium hover:bg-[#FE5000] hover:text-white transition-all mobile:hidden laptop:block">
          Cancel
        </button>
        <button @click="submit"
          class="px-10 py-2 border-solid border-[1px] border-[#092E87] text-[#092E87] rounded-full font-medium hover:bg-[#092E87] hover:text-white transition-all">
          Continue
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mapActions,
  mapGetters,
} from "vuex";

import { useToast } from "vue-toastification";
const toast = useToast();
export default {
  name: 'financingModal',
  data() {
    return {
      isVisible: false,
      selectedPlanId: null,
    }
  },

  computed: {
    ...mapGetters(["financialPlans"]),
  },

  watch: {
    financialPlans: function () {
      this.selectedPlanId = null
      document.documentElement.style.overflow = 'hidden';
      this.isVisible = true;
    },
  },

  methods: {
    ...mapActions(["getFinancialPlans"]),

    async show(id) {
      this.getFinancialPlans({
        institute_id: id
      })
    },

    close() {
      document.documentElement.style.overflow = '';
      this.isVisible = false;
    },
    submit() {
      if (this.selectedPlanId) {
        this.$emit('selected', this.selectedPlanId);
        this.close();
      } else {
        toast.error("Please select a plan.");
      }
    }
  }
}
</script>

<style scoped>
.animate-in {
  animation: animate-in 0.2s ease-out;
}

@keyframes animate-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 2px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #092E87;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #061d56;
}
</style>
<style>
  .description--text u{
      font-size: 14px;
    }
@media (max-width: 1024px) {
  .description--text u{
    font-size: 13px !important;
  }
}
</style>